import { getEnv, Instance, types } from "mobx-state-tree";
import Language from "../../../translation/language";

import { RequiredValidatorModel } from "../required/RequiredValidator";
import { ValidationMesssageModel } from "../ValidationMesssage";
import { ValidatorModel } from "../Validator";
import { NumberValidatorModel } from "./NumberValidator";

export const RequiredDecimalProperties = types.model({
  precision: types.maybeNull(types.number),
  length: types.maybeNull(types.number),
  maxValue: types.maybeNull(types.number),
  isRequired: types.boolean,
  requiredValidator: types.optional(RequiredValidatorModel, {}),
});

export const DecimalNumberValidatorModel = types
  .compose(ValidatorModel, NumberValidatorModel, RequiredDecimalProperties)
  .actions((self) => {
    const getDecimalNumberTooLongMessageContext = (
      totalLength: number,
      precision: number | null
    ) => {
      const maxIntegerPart = `${Math.pow(10, totalLength - (precision ?? 0)) - 1}`;
      const maxFractionalPart =
        (precision && `${getDecimalSeparator()}${Math.pow(10, precision) - 1}`) || "";
      const maximumNumberValue = maxIntegerPart + maxFractionalPart;
      return { maximumNumberValue };
    };

    const formattedCorrectly = (value: string) => {
      switch (getEnv(self).translationService.language) {
        case Language.english:
          return RegExp(/^\d+(\.\d*[0-9])?$/).test(value);
        case Language.german:
          return RegExp(/^\d+(,\d*[0-9])?$/).test(value);
      }
    };

    const getDecimalSeparator = () => {
      if (getEnv(self).translationService.language === Language.english) return ".";
      return ",";
    };
    return {
      validate(value: any) {
        const fieldValueAsString: string | undefined = value ? String(value).trim() : undefined;

        if (self.isRequired) {
          const result = self.requiredValidator.validate(fieldValueAsString);
          if (result && result.message.mainKey) return result;
        }

        if (!fieldValueAsString) return undefined;
        if (!self.isANumber(fieldValueAsString) || fieldValueAsString === "") {
          return ValidationMesssageModel.create({
            message: { mainKey: "general.validation.number" },
          });
        }

        if (!formattedCorrectly(fieldValueAsString)) {
          return ValidationMesssageModel.create({
            message: { mainKey: "general.validation.number.format" },
          });
        }

        const [integerPart = "", fractionalPart = ""] = fieldValueAsString.split(
          getDecimalSeparator()
        );

        if (self.precision === 0 && fieldValueAsString !== integerPart)
          return ValidationMesssageModel.create({
            message: { mainKey: "general.validation.number-without-decimals" },
          });

        if (self.precision && fractionalPart.length > self.precision)
          return ValidationMesssageModel.create({
            message: { mainKey: "general.validation.number-of-decimals" },
          });

        if (self.maxValue && self.numberTooLarge(self.maxValue, fieldValueAsString)) {
          return self.numberTooLargeMessage(self.maxValue)
        }

        if (self.length && self.precision && integerPart.length > self.length - self.precision)
          return ValidationMesssageModel.create({
            message: { mainKey: "general.validation.number-length-long" },
            context: getDecimalNumberTooLongMessageContext(self.length, self.precision),
          });

        if (self.length && self.numberTooLong(self.length, integerPart, fractionalPart)) {
          return ValidationMesssageModel.create({
            message: { mainKey: "general.validation.number-length-long" },
            context: getDecimalNumberTooLongMessageContext(self.length, self.precision),
          });
        }
      },
    };
  });

export type DecimalNumberValidator = Instance<typeof DecimalNumberValidatorModel>;
